import { ToggleGroup as TamaguiToggleGroup, XStack } from 'tamagui';

import { Icon, type IconType } from '../icons/Icon';
import { BodyMediumRegular, LabelRegular } from '../text/Typography';

interface SegmentedControlOption<T> {
    value: T;
    label: string;
    icon?: IconType;
    showDot?: boolean;
    /** When component width is 'full', this can be used to increase the width of specific option labels, i.e to prevent wrapping */
    flexBasis?: number;
}

interface SegmentedControlProps<T> {
    selected: T;
    defaultValue: T;
    onValueChange: (val: T) => void;
    options: Array<SegmentedControlOption<T>>;
    size?: 'small' | 'large';
    width?: 'auto' | 'full';
    /** If true, the selected option can be deselected by clicking on it */
    allowDeselection?: boolean;
}

export const SegmentedControl = <T extends string>({
    selected,
    onValueChange,
    options,
    size = 'large',
    width = 'full',
    allowDeselection = false,
}: SegmentedControlProps<T>) => {
    const isLarge = size === 'large';
    const isFullWidth = width === 'full';
    return (
        <TamaguiToggleGroup
            type="single"
            value={selected}
            orientation="horizontal"
            width={isFullWidth ? '100%' : 'auto'}
            gap={1}
            onValueChange={onValueChange}
            unstyled
            disableDeactivation={!allowDeselection}
            backgroundColor="$gray400"
            borderRadius={isLarge ? 16 : 12}
            padding={2}
            cursor="pointer"
        >
            {options.map((option) => {
                const isSelected = selected === option.value;
                return (
                    <TamaguiToggleGroup.Item
                        key={option.value}
                        value={option.value}
                        unstyled
                        disablePassStyles
                        flex={isFullWidth ? 1 : 0}
                        flexDirection="row"
                        alignItems="stretch"
                        justifyContent="center"
                        {...(isFullWidth && {
                            flexBasis: option.flexBasis ?? 0,
                        })}
                    >
                        <XStack
                            width={isFullWidth ? '100%' : 'auto'}
                            backgroundColor={
                                isSelected ? '$gray200' : 'transparent'
                            }
                            borderRadius={isLarge ? 14 : 10}
                            paddingVertical={isLarge ? 10 : 8}
                            paddingHorizontal={isLarge ? 24 : 16}
                            minHeight={isLarge ? 44 : 36}
                            alignItems="center"
                            justifyContent="center"
                            gap={isLarge ? 8 : 6}
                        >
                            {option.icon && (
                                <XStack width={isLarge ? 24 : 16}>
                                    <Icon
                                        icon={option.icon}
                                        size={isLarge ? 24 : 16}
                                        color={
                                            isSelected ? '$black' : '$gray1100'
                                        }
                                    />
                                </XStack>
                            )}
                            {isLarge ? (
                                <BodyMediumRegular
                                    textAlign="center"
                                    lineHeight={17}
                                    color={isSelected ? '$black' : '$gray1100'}
                                >
                                    {option.label}
                                </BodyMediumRegular>
                            ) : (
                                <LabelRegular
                                    textAlign="center"
                                    lineHeight={13}
                                    color={isSelected ? '$black' : '$gray1100'}
                                >
                                    {option.label}
                                </LabelRegular>
                            )}
                            {option.showDot && (
                                <XStack
                                    width={isLarge ? 6 : 4}
                                    height={isLarge ? 6 : 4}
                                    borderRadius={6}
                                    backgroundColor="$orange900"
                                />
                            )}
                        </XStack>
                    </TamaguiToggleGroup.Item>
                );
            })}
        </TamaguiToggleGroup>
    );
};
